import React, { useEffect, useState } from "react";
import { trackFeatureAction } from "../../../analytics/googleAnalytics";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchOxyzoCurrentDues,
  fetchFilteredOxyzoCurrentDues,
  downloadOxyzoCurrentDuesReport,
} from "../../../redux/slice/oxyzoCurrentDuesSlice";
import { HashLoader } from "react-spinners";
import { Card, Button, Input, Select, Space, Alert, Empty } from "antd";
import { StyledTable, StyledButton } from "../components/styleComp";
import { useAuth } from "../../../context/AuthContext";
import jwt_decode from "jwt-decode";

const { Option } = Select;

const OxyzoCurrentDues = (props) => {
  const [buttonLoading, setButtonLoading] = useState(false);

  const { authState } = useAuth();
  const dispatch = useDispatch();

  const [state, setState] = useState({
    pages: { oxyzoCurrentDues: { page: 1, pageSize: 7 } },
    filters: { oxyzoCurrentDues: {} },
    searchInputs: { oxyzoCurrentDues: "" },
    selectedColumns: { oxyzoCurrentDues: "" },
    hasSearched: { oxyzoCurrentDues: false },
  });

  const jwtBearer = authState?.token;
  const tokenDecoded = jwtBearer ? jwt_decode(jwtBearer) : null;

  useEffect(() => {
    if (jwtBearer) {
      const { page, pageSize } = state?.pages?.oxyzoCurrentDues;
      dispatch(
        fetchOxyzoCurrentDues({
          jwtBearer,
          page,
          filters: state?.filters?.oxyzoCurrentDues,
          pageSize,
        })
      );
    }
  }, [jwtBearer, state?.pages?.oxyzoCurrentDues, state?.filters?.oxyzoCurrentDues, dispatch]);

  const {
    data: currentDuesData = [],
    filteredData: filteredCurrentDuesData = [],
    totalCount: totalCurrentDuesCount,
    loading: isLoadingCurrentDues,
    filteredLoading: isLoadingFilteredCurrentDues,
    error: errorCurrentDues,
  } = useSelector((state) => state?.oxyzoCurrentDues || {});

  const defineColumns = (data) => {
    if (!data || data?.length === 0) return [];
    const keys = Object.keys(data[0]);
    return keys.map((key) => ({
      title: <strong style={{ fontSize: "18px" }}>{key.replace(/_/g, " ").toUpperCase()}</strong>,
      dataIndex: key,
      key: key,
      render: (text) => <span style={{ fontSize: "16px" }}>{text}</span>,
    }));
  };

  const handleKeyDown = (event, type) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleFilter(type);
    }
  };

  const handleFilter = (type) => {
    trackFeatureAction("Reports", "Current Dues Report", "search");
    const columnName = state?.selectedColumns?.[type]?.toLowerCase();
    const trimmedInput = state?.searchInputs?.[type]?.trim();

    dispatch(
      fetchFilteredOxyzoCurrentDues({
        jwtBearer,
        filters: { [columnName]: trimmedInput },
      })
    );

    setState((prevState) => ({
      ...prevState,
      hasSearched: { ...prevState?.hasSearched, [type]: true },
      pages: {
        ...prevState?.pages,
        [type]: { ...prevState?.pages?.[type], page: 1 },
      },
    }));
  };

  const handleReset = (type) => {
    trackFeatureAction("Reports", "Current Dues Report", "reset");
    setState((prevState) => ({
      ...prevState,
      searchInputs: { ...prevState?.searchInputs, [type]: "" },
      selectedColumns: { ...prevState?.selectedColumns, [type]: "" },
      hasSearched: { ...prevState?.hasSearched, [type]: false },
      filters: { ...prevState?.filters, [type]: {} },
    }));
  };

  const paginationConfig = (type) => {
    const total = state?.hasSearched?.oxyzoCurrentDues
      ? filteredCurrentDuesData?.length
      : totalCurrentDuesCount;

    return {
      current: state?.pages?.oxyzoCurrentDues?.page,
      pageSize: state?.pages?.oxyzoCurrentDues?.pageSize,
      total: total,
      showTotal: (total, [start, end]) => `${start}-${end} of ${total} items`,
      onChange: (page, pageSize) =>
        setState((prevState) => ({
          ...prevState,
          pages: { ...prevState?.pages, [type]: { page, pageSize } },
        })),
    };
  };

  const renderSearchBar = (columns, type) => (
    <Space style={{ marginBottom: 16, marginLeft: 2 }}>
      <Select
        placeholder="Select Column"
        style={{ width: 200, height: 32, lineHeight: "32px" }}
        value={state?.selectedColumns?.[type] || undefined}
        onChange={(value) =>
          setState((prevState) => ({
            ...prevState,
            selectedColumns: { ...prevState?.selectedColumns, [type]: value },
          }))
        }
      >
        {columns?.map((col) => (
          <Option key={col?.key} value={col?.dataIndex}>
            {col?.title?.props?.children}
          </Option>
        ))}
      </Select>
      <Input
        placeholder="Value"
        value={state?.searchInputs?.[type]}
        onChange={(e) =>
          setState((prevState) => ({
            ...prevState,
            searchInputs: { ...prevState?.searchInputs, [type]: e.target.value },
          }))
        }
        onKeyDown={(e) => handleKeyDown(e, type)}
        style={{ width: 150, height: 32, lineHeight: "32px" }}
        disabled={!state?.selectedColumns?.[type]}
      />
      <Button
        type="primary"
        onClick={() => handleFilter(type)}
        style={{ height: 32, lineHeight: "32px" }}
      >
        Search
      </Button>
      {state?.hasSearched?.oxyzoCurrentDues && (
        <Button
          onClick={() => handleReset(type)}
          style={{ width: 70, height: 32, lineHeight: "32px" }}
        >
          Reset
        </Button>
      )}
    </Space>
  );

  const handleAPIDownload = () => {
    trackFeatureAction("Reports", "Current Dues Report", "download");
    setButtonLoading(true);
    dispatch(downloadOxyzoCurrentDuesReport({ jwtBearer }))
      .finally(() => setButtonLoading(false));
  };

  return (
    <Card
      title={
        <span style={{ fontWeight: 600, fontSize: "24px", fontFamily: "poppins-font, sans-serif" }}>
          CURRENT DUES REPORT
        </span>
      }
      bordered
      extra={
        <div style={{ display: "flex", flexDirection: "row", gap: "20px" }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <StyledButton
              type="primary"
              onClick={handleAPIDownload}
              loading={buttonLoading}
            >
              Download Report
            </StyledButton>
          </div>
        </div>
      }
    >
      {renderSearchBar(defineColumns(currentDuesData || []), "oxyzoCurrentDues")}

      {isLoadingFilteredCurrentDues || isLoadingCurrentDues ? (
        <div className="spinner-container">
          <HashLoader color="#f57171" loading={true} />
        </div>
      ) : errorCurrentDues ? (
        <Alert message={errorCurrentDues} type="error" />
      ) : (
        <StyledTable
          columns={defineColumns(currentDuesData)}
          dataSource={
            state?.hasSearched?.oxyzoCurrentDues ? filteredCurrentDuesData : currentDuesData
          }
          pagination={paginationConfig("oxyzoCurrentDues")}
          locale={{ emptyText: <Empty description="No data available" /> }}
        />
      )}
    </Card>
  );
};

export default OxyzoCurrentDues;
